
import { useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { useToast } from '@/components/ui/use-toast';
import { useDataFetching } from './data-management/useDataFetching';
import { useDataMutation } from './data-management/useDataMutation';

export const useDataManagement = () => {
    const {  user, activeAccountId } = useAuth();
    const { toast } = useToast();
    
    // Destructing properties from useDataFetching
    // NOTA: Algumas propriedades antigas (sales, receipts) foram removidas ou alteradas.
    // Estamos extraindo as novas funções de fetch aqui.
    const { 
        // Lookups
        operators,
        setOperators,
        flags,
        setFlags,
        paymentMethods,
        setPaymentMethods,
        
        // Novas Funções de Fetch (Server-Side)
        fetchSales,
        fetchSalesSummary,
        fetchReceipts,
        fetchReceiptsSummary,
        
        // Gatilho de atualização instantânea
        forceReloadData,
        refreshTrigger,
        
        // Utils
        checkOrphanReceipts,
        
    } = useDataFetching();

    // Estados de compatibilidade para mutations (sales/receipts são gerenciados server-side)
    const sales = []; // Placeholder
    const receipts = []; // Placeholder
    const setSales = () => {}; // No-op (server-side)
    const setReceipts = () => {}; // No-op (server-side)
    const setTotalSales = () => {};
    const setTotalReceipts = () => {};

    const [aliasModalState, setAliasModalState] = useState({ isOpen: false, alias: '', type: '', resolve: null, onAddNew: null });

    const openAliasModal = useCallback((alias, type, onAddNew) => {
        return new Promise((resolve) => {
            setAliasModalState({ isOpen: true, alias, type, resolve, onAddNew });
        });
    }, []);

    const closeAliasModal = () => {
        aliasModalState.resolve?.(null);
        setAliasModalState({ isOpen: false, alias: '', type: '', resolve: null, onAddNew: null });
    };

    const resolveAliasModal = (canonicalName) => {
        aliasModalState.resolve?.(canonicalName);
        setAliasModalState({ isOpen: false, alias: '', type: '', resolve: null, onAddNew: null });
    };

    const dataRepos = useMemo(() => ({ sales, receipts, operators, flags, paymentMethods }), [operators, flags, paymentMethods]);
    const stateSetters = useMemo(() => ({ setSales, setReceipts, setOperators, setFlags, setPaymentMethods, setTotalSales, setTotalReceipts }), []);
    
    
    const mutations = useDataMutation({ 
        user, 
        activeAccountId,
        toast, 
        ...dataRepos, 
        ...stateSetters, 
        openAliasModal,
        forceReloadData,
        salesPerPage: 50,
        receiptsPerPage: 50
    });

    return useMemo(() => ({
        // Dados de Lookup
        operators,
        flags,
        paymentMethods,
        
        loading: false, // Agora gerenciado por tabela
        
        // Modal
        aliasModalState,
        closeAliasModal,
        resolveAliasModal,
        
        // Novas Funções API
        fetchSales,
        fetchSalesSummary,
        fetchReceipts,
        fetchReceiptsSummary,
        
        // Gatilho de atualização instantânea
        refreshTrigger,
        
        // Utils
        forceReloadData,
        checkOrphanReceipts,
        
        // Mutations
        ...mutations,
    }), [
        operators, flags, paymentMethods, 
        aliasModalState, 
        fetchSales, fetchSalesSummary, fetchReceipts, fetchReceiptsSummary, 
        refreshTrigger, forceReloadData, checkOrphanReceipts, 
        mutations
    ]);
};

